/**
 * Colección y búsqueda: filtros y ordenación sin recargar, panel de filtros
 * en el móvil y botón «Cargar más». Usa la Section Rendering API.
 */
import { debounce, fetchSection } from '@theme/global';

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.panel = this.querySelector('[data-facets-panel]');
    this.submitLater = debounce(() => this.submitForm(), 600);

    this.addEventListener('change', (event) => {
      if (!event.target.closest('[data-facets-form]')) return;
      if (event.target.type === 'number') this.submitLater();
      else this.submitForm();
    });

    this.addEventListener('submit', (event) => {
      if (!event.target.closest('[data-facets-form]')) return;
      event.preventDefault();
      this.submitForm();
      this.closePanel();
    });

    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.load(link.href);
        return;
      }
      const more = event.target.closest('[data-load-more-button]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }
      if (event.target.closest('[data-facets-open]')) this.openPanel();
      if (event.target.closest('[data-facets-close]')) this.closePanel();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel?.classList.contains('is-open')) this.closePanel();
    });

    this.onPopState = () => this.load(window.location.href, false);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  submitForm() {
    const form = this.querySelector('[data-facets-form]');
    if (!form) return;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const url = new URL(form.getAttribute('action'), window.location.origin);
    url.search = params.toString();
    this.load(url.toString());
  }

  async load(url, push = true) {
    const activeId = document.activeElement?.id;
    const openFacets = [...this.querySelectorAll('details.facet[open]')].map((details) => details.id);
    this.classList.add('is-loading');

    try {
      const doc = await fetchSection(url, this.sectionId);
      ['[data-results]', '[data-product-count]', '[data-active-facets]'].forEach((selector) => {
        const current = this.querySelector(selector);
        const next = doc.querySelector(selector);
        if (current && next) current.replaceWith(next);
      });

      const body = this.querySelector('[data-facets-body]');
      const nextBody = doc.querySelector('[data-facets-body]');
      if (body && nextBody) {
        body.innerHTML = nextBody.innerHTML;
        openFacets.forEach((id) => {
          const details = id && document.getElementById(id);
          if (details) details.open = true;
        });
      }

      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      if (push) window.history.pushState({}, '', url);
    } catch (err) {
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async loadMore(button) {
    button.classList.add('is-loading');
    try {
      const doc = await fetchSection(button.href, this.sectionId);
      const grid = this.querySelector('[data-results] [data-product-grid]');
      const items = doc.querySelectorAll('[data-results] [data-grid-item]');
      const firstNew = items[0];
      grid?.append(...items);
      firstNew?.querySelector('a.card__link')?.focus({ preventScroll: true });

      const wrapper = this.querySelector('[data-load-more]');
      const nextWrapper = doc.querySelector('[data-load-more]');
      if (wrapper) {
        if (nextWrapper) wrapper.replaceWith(nextWrapper);
        else wrapper.remove();
      }
    } catch (err) {
      window.location.href = button.href;
    }
  }

  openPanel() {
    if (!this.panel) return;
    this.lastOpener = document.activeElement;
    this.panel.classList.add('is-open');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    document.documentElement.style.overflow = 'hidden';
    this.querySelectorAll('[data-facets-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    this.panel.querySelector('[data-facets-close]')?.focus();
  }

  closePanel() {
    if (!this.panel?.classList.contains('is-open')) return;
    this.panel.classList.remove('is-open');
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    document.documentElement.style.overflow = '';
    this.querySelectorAll('[data-facets-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.lastOpener?.focus();
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
